import { useNavigate } from "react-router-dom"
import {
  Building2,
  Briefcase,
  CheckSquare,
  Wrench,
  FileText,
  Receipt,
  File,
  Mail,
  Phone,
  Clock,
} from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { formatDate, formatCurrency } from "@/lib/utils"
import type {
  RelatedContact,
  RelatedDeal,
  RelatedTask,
  RelatedServiceTicket,
  RelatedDocument,
  RelatedQuote,
  RelatedInvoice,
} from "@/hooks/use-entity-related"

// ── Contacts ──

export function ContactsList({ items }: { items: RelatedContact[] }) {
  const navigate = useNavigate()
  if (items.length === 0) return <EmptyState label="Brak osob kontaktowych" />

  return (
    <div className="divide-y divide-border">
      {items.map((c) => (
        <div
          key={c.id}
          className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-muted/30"
          onClick={() => navigate(`/contacts/${c.id}`)}
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground flex-shrink-0">
            {c.first_name[0]}{c.last_name[0]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground">
              {c.full_name}
            </p>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              {c.email && (
                <span className="flex items-center gap-1">
                  <Mail size={10} /> {c.email}
                </span>
              )}
              {c.phone && (
                <span className="flex items-center gap-1">
                  <Phone size={10} /> {c.phone}
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Companies ──

export function CompaniesList({ items }: { items: { id: string; name: string }[] }) {
  const navigate = useNavigate()
  if (items.length === 0) return <EmptyState label="Brak firm" />

  return (
    <div className="divide-y divide-border">
      {items.map((c) => (
        <div
          key={c.id}
          className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-muted/30"
          onClick={() => navigate(`/companies/${c.id}`)}
        >
          <Building2 size={16} className="text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">
            {c.name}
          </p>
        </div>
      ))}
    </div>
  )
}

// ── Deals ──

const DEAL_STATUS_COLORS: Record<string, string> = {
  open: "var(--primary)",
  won: "#22C55E",
  lost: "#EF4444",
}

const DEAL_STATUS_LABELS: Record<string, string> = {
  open: "Otwarta",
  won: "Wygrana",
  lost: "Przegrana",
}

export function DealsList({ items }: { items: RelatedDeal[] }) {
  const navigate = useNavigate()
  if (items.length === 0) return <EmptyState label="Brak transakcji" />

  return (
    <div className="divide-y divide-border">
      {items.map((d) => (
        <div
          key={d.id}
          className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-muted/30"
          onClick={() => navigate(`/deals/${d.id}`)}
        >
          <Briefcase size={16} className="text-muted-foreground" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium truncate text-foreground">
                {d.name}
              </p>
              <Badge
                variant="outline"
                className="text-[10px] flex-shrink-0"
                style={{
                  borderColor: DEAL_STATUS_COLORS[d.status],
                  color: DEAL_STATUS_COLORS[d.status],
                }}
              >
                {DEAL_STATUS_LABELS[d.status] ?? d.status}
              </Badge>
              {d.phase && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <div className="h-2 w-2 rounded-full" style={{ backgroundColor: d.phase.color }} />
                  <span className="text-xs text-muted-foreground">{d.phase.name}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs mt-0.5 text-muted-foreground">
              {d.value != null && (
                <span className="font-mono">{formatCurrency(d.value, d.currency)}</span>
              )}
              {d.owner && <span>{d.owner.first_name} {d.owner.last_name}</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Tasks ──

const TASK_PRIORITY_COLORS: Record<string, string> = {
  low: "#6B7280",
  normal: "#3B82F6",
  high: "#F59E0B",
  urgent: "#EF4444",
}

const TASK_STATUS_LABELS: Record<string, string> = {
  open: "Otwarte",
  in_progress: "W trakcie",
  completed: "Wykonane",
  cancelled: "Anulowane",
}

export function TasksList({ items }: { items: RelatedTask[] }) {
  const navigate = useNavigate()
  if (items.length === 0) return <EmptyState label="Brak zadan" />

  return (
    <div className="divide-y divide-border">
      {items.map((t) => {
        const isCompleted = t.status === "completed" || t.status === "cancelled"
        const isOverdue = t.due_date && !isCompleted && new Date(t.due_date) < new Date()

        return (
          <div
            key={t.id}
            className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-muted/30"
            onClick={() => navigate(`/tasks/${t.id}`)}
          >
            <CheckSquare
              size={16}
              className={isCompleted ? "text-green-500" : "text-muted-foreground"}
              style={!isCompleted && TASK_PRIORITY_COLORS[t.priority] ? { color: TASK_PRIORITY_COLORS[t.priority] } : undefined}
            />
            <div className="flex-1 min-w-0">
              <p
                className={`text-sm font-medium truncate ${isCompleted ? "text-muted-foreground line-through" : "text-foreground"}`}
              >
                {t.title}
              </p>
              <div className="flex items-center gap-3 text-xs mt-0.5 text-muted-foreground">
                <span>{TASK_STATUS_LABELS[t.status] ?? t.status}</span>
                {t.due_date && (
                  <span
                    className={`flex items-center gap-1 ${isOverdue ? "text-red-500" : ""}`}
                  >
                    <Clock size={10} /> {formatDate(t.due_date)}
                  </span>
                )}
                {t.assignee && <span>{t.assignee.first_name} {t.assignee.last_name}</span>}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Service Tickets ──

const TICKET_PRIORITY_LABELS: Record<string, string> = {
  low: "Niski",
  medium: "Sredni",
  high: "Wysoki",
  urgent: "Pilny",
}

const TICKET_STATUS_LABELS: Record<string, string> = {
  open: "Otwarty",
  resolved: "Rozwiazany",
  closed: "Zamkniety",
}

const TICKET_STATUS_COLORS: Record<string, string> = {
  open: "var(--primary)",
  resolved: "#22C55E",
  closed: "#6B7280",
}

export function ServiceTicketsList({ items }: { items: RelatedServiceTicket[] }) {
  const navigate = useNavigate()
  if (items.length === 0) return <EmptyState label="Brak zgloszen serwisowych" />

  return (
    <div className="divide-y divide-border">
      {items.map((s) => (
        <div
          key={s.id}
          className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-muted/30"
          onClick={() => navigate(`/service/${s.id}`)}
        >
          <Wrench size={16} className="text-muted-foreground" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium truncate text-foreground">
                {s.title}
              </p>
              <Badge
                variant="outline"
                className="text-[10px] flex-shrink-0"
                style={{
                  borderColor: TICKET_STATUS_COLORS[s.status],
                  color: TICKET_STATUS_COLORS[s.status],
                }}
              >
                {TICKET_STATUS_LABELS[s.status] ?? s.status}
              </Badge>
              <Badge
                variant="outline"
                className="text-[10px] flex-shrink-0"
                style={{
                  borderColor: TASK_PRIORITY_COLORS[s.priority],
                  color: TASK_PRIORITY_COLORS[s.priority],
                }}
              >
                {TICKET_PRIORITY_LABELS[s.priority] ?? s.priority}
              </Badge>
            </div>
            <div className="flex items-center gap-3 text-xs mt-0.5 text-muted-foreground">
              {s.phase && (
                <span className="flex items-center gap-1">
                  <div className="h-2 w-2 rounded-full" style={{ backgroundColor: s.phase.color }} />
                  {s.phase.name}
                </span>
              )}
              {s.assigned_to && <span>{s.assigned_to.first_name} {s.assigned_to.last_name}</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Documents ──

function formatFileSize(bytes: number | null): string {
  if (!bytes) return ""
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1048576).toFixed(1)} MB`
}

export function DocumentsList({ items }: { items: RelatedDocument[] }) {
  const navigate = useNavigate()
  if (items.length === 0) return <EmptyState label="Brak dokumentow" />

  return (
    <div className="divide-y divide-border">
      {items.map((d) => (
        <div
          key={d.id}
          className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-muted/30"
          onClick={() => navigate("/documents")}
        >
          <File size={16} className="text-muted-foreground" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate text-foreground">
              {d.name}
            </p>
            <div className="flex items-center gap-3 text-xs mt-0.5 text-muted-foreground">
              {d.content_type && <span>{d.content_type}</span>}
              {d.file_size != null && <span>{formatFileSize(d.file_size)}</span>}
              {d.uploaded_by && <span>{d.uploaded_by.first_name} {d.uploaded_by.last_name}</span>}
              <span>{formatDate(d.created_at)}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Quotes ──

const QUOTE_STATUS_LABELS: Record<string, string> = {
  draft: "Szkic",
  sent: "Wyslana",
  viewed: "Obejrzana",
  accepted: "Zaakceptowana",
  rejected: "Odrzucona",
  expired: "Wygasla",
  cancelled: "Anulowana",
}

const QUOTE_STATUS_COLORS: Record<string, string> = {
  draft: "#6B7280",
  sent: "#3B82F6",
  viewed: "#8B5CF6",
  accepted: "#22C55E",
  rejected: "#EF4444",
  expired: "#F59E0B",
  cancelled: "#6B7280",
}

export function QuotesList({ items }: { items: RelatedQuote[] }) {
  const navigate = useNavigate()
  if (items.length === 0) return <EmptyState label="Brak ofert" />

  return (
    <div className="divide-y divide-border">
      {items.map((q) => (
        <div
          key={q.id}
          className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-muted/30"
          onClick={() => navigate(`/quotes/${q.id}`)}
        >
          <FileText size={16} className="text-muted-foreground" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-foreground">
                {q.number}
              </p>
              <Badge
                variant="outline"
                className="text-[10px] flex-shrink-0"
                style={{
                  borderColor: QUOTE_STATUS_COLORS[q.status],
                  color: QUOTE_STATUS_COLORS[q.status],
                }}
              >
                {QUOTE_STATUS_LABELS[q.status] ?? q.status}
              </Badge>
            </div>
            <div className="flex items-center gap-3 text-xs mt-0.5 text-muted-foreground">
              <span className="font-mono">{formatCurrency(q.total_gross, q.currency)}</span>
              {q.valid_until && <span>Wazna do: {formatDate(q.valid_until)}</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Invoices ──

const INVOICE_STATUS_LABELS: Record<string, string> = {
  draft: "Szkic",
  issued: "Wystawiona",
  sent: "Wyslana",
  paid: "Zaplacona",
  partially_paid: "Czesciowo zaplacona",
  overdue: "Po terminie",
  cancelled: "Anulowana",
  corrected: "Skorygowana",
}

const INVOICE_STATUS_COLORS: Record<string, string> = {
  draft: "#6B7280",
  issued: "#3B82F6",
  sent: "#8B5CF6",
  paid: "#22C55E",
  partially_paid: "#F59E0B",
  overdue: "#EF4444",
  cancelled: "#6B7280",
  corrected: "#F97316",
}

const INVOICE_TYPE_LABELS: Record<string, string> = {
  vat: "VAT",
  proforma: "Proforma",
  corrective: "Korygujaca",
  margin: "Marza",
}

export function InvoicesList({ items }: { items: RelatedInvoice[] }) {
  const navigate = useNavigate()
  if (items.length === 0) return <EmptyState label="Brak faktur" />

  return (
    <div className="divide-y divide-border">
      {items.map((inv) => (
        <div
          key={inv.id}
          className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-muted/30"
          onClick={() => navigate(`/invoices/${inv.id}`)}
        >
          <Receipt size={16} className="text-muted-foreground" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-foreground">
                {inv.number}
              </p>
              <Badge variant="outline" className="text-[10px] flex-shrink-0">
                {INVOICE_TYPE_LABELS[inv.invoice_type] ?? inv.invoice_type}
              </Badge>
              <Badge
                variant="outline"
                className="text-[10px] flex-shrink-0"
                style={{
                  borderColor: INVOICE_STATUS_COLORS[inv.status],
                  color: INVOICE_STATUS_COLORS[inv.status],
                }}
              >
                {INVOICE_STATUS_LABELS[inv.status] ?? inv.status}
              </Badge>
            </div>
            <div className="flex items-center gap-3 text-xs mt-0.5 text-muted-foreground">
              <span className="font-mono">{formatCurrency(inv.total_gross)}</span>
              {inv.amount_due > 0 && (
                <span className="text-red-500">
                  Do zaplaty: {formatCurrency(inv.amount_due)}
                </span>
              )}
              {inv.due_date && <span>Termin: {formatDate(inv.due_date)}</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Empty State ──

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-input py-8 text-muted-foreground">
      <p className="text-sm">{label}</p>
    </div>
  )
}
